import { haversineDistance } from "../utils/geo";
import { mergeMaintenanceRecord } from "../utils/placeMaintenance";

// Synthesized place attributes (permits, dogs, kids, parking, …) with a
// citation per claim, produced by the scripts/enrich pipeline from official
// sources only and served as static files from public/enrichment/.
// index.json lists region files with their coverage circles.
const BASE = "/enrichment";

let indexPromise = null;
const regionCache = new Map(); // file -> Promise<places[]>
let maintenancePromise;

function loadMaintenance() {
  if (!maintenancePromise) {
    maintenancePromise = fetch(`${BASE}/maintenance.json`)
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }
  return maintenancePromise;
}

function loadIndex() {
  if (!indexPromise) {
    indexPromise = fetch(`${BASE}/index.json`)
      .then((r) => (r.ok ? r.json() : []))
      .catch(() => []);
  }
  return indexPromise;
}

function loadRegion(file) {
  if (!regionCache.has(file)) {
    regionCache.set(
      file,
      fetch(`${BASE}/${file}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((region) => region?.places ?? [])
        .catch(() => []),
    );
  }
  return regionCache.get(file);
}

async function loadRecordsCovering(lat, lon) {
  const index = await loadIndex();
  const covering = index.filter(
    (r) => haversineDistance(lat, lon, r.lat, r.lon) <= r.radiusMiles,
  );
  const lists = await Promise.all(covering.map((r) => loadRegion(r.file)));
  const maintenance = await loadMaintenance();
  return lists
    .flat()
    .map((record) => mergeMaintenanceRecord(record, maintenance));
}

function numericOsmId(id) {
  const m = /(\d+)$/.exec(String(id || ""));
  return m ? m[1] : null;
}

function nameTokens(name) {
  return new Set(
    String(name || "")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .split(/\s+/)
      .filter(Boolean),
  );
}

function tokensContain(a, b) {
  if (!a.size || !b.size) return false;
  const [small, large] = a.size <= b.size ? [a, b] : [b, a];
  for (const t of small) if (!large.has(t)) return false;
  return true;
}

/**
 * Match one app POI ({id, name, lat, lon}) against enrichment records.
 * OSM numeric id is the primary key (with a proximity sanity check, since
 * node/way ids can collide numerically); name-token containment nearby is
 * the fallback so viewpoint/waterfall twins of the same place still match.
 */
export function matchEnrichment(place, records) {
  const numId = numericOsmId(place.id);
  const tokens = nameTokens(place.name);

  let best = null;
  let bestDist = Infinity;
  for (const record of records) {
    const dist =
      place.lat != null && record.lat != null
        ? haversineDistance(place.lat, place.lon, record.lat, record.lon)
        : Infinity;

    if (numId && numericOsmId(record.id) === numId && dist < 0.5) return record;

    if (
      dist < 0.15 &&
      tokensContain(tokens, nameTokens(record.name)) &&
      dist < bestDist
    ) {
      best = record;
      bestDist = dist;
    }
  }
  return best;
}

/**
 * Resolve enrichment for a list of app POIs.
 * Returns a Map of place.id -> enrichment record (misses simply absent).
 */
export async function getEnrichmentForPlaces(places = []) {
  const result = new Map();
  const anchor = places.find((p) => p.lat != null && p.lon != null);
  if (!anchor) return result;

  const records = await loadRecordsCovering(anchor.lat, anchor.lon);
  if (!records.length) return result;

  for (const place of places) {
    const match = matchEnrichment(place, records);
    if (match) result.set(place.id, match);
  }
  return result;
}
